import { useEffect, useState, type FormEvent } from 'react';
import {
  ApiError,
  createProperty,
  deleteProperty,
  listProperties,
  updateProperty,
  type Property,
  type PropertyInput,
  type PropertyOwner,
} from '../api';

const KIND_SUGGESTIONS = ['Main residence', 'Buy-to-let', 'Holiday let', 'Commercial', 'Other'];

// FormOwnerRow keeps share_pct as a string so an in-progress edit
// (e.g. typing "37." before adding the second decimal) doesn't get
// coerced to NaN by Number(). Coerced + validated at submit time.
interface FormOwnerRow {
  name: string;
  share_pct: string;
}

interface FormState {
  name: string;
  owners: FormOwnerRow[];
  kind: string;
  address: string;
  purchase_price_pounds: string;
  purchase_date: string;
  current_value_pounds: string;
  value_as_of: string;
  mortgage_balance_pounds: string;
  mortgage_interest_rate_pct: string;
  mortgage_monthly_payment_pounds: string;
  notes: string;
}

const emptyForm: FormState = {
  name: '',
  owners: [{ name: '', share_pct: '100' }],
  kind: '',
  address: '',
  purchase_price_pounds: '',
  purchase_date: '',
  current_value_pounds: '',
  value_as_of: todayISO(),
  mortgage_balance_pounds: '',
  mortgage_interest_rate_pct: '',
  mortgage_monthly_payment_pounds: '',
  notes: '',
};

export function PropertiesSection({
  accountId,
  onPropertiesLoad,
}: {
  accountId: string;
  // Surface load to the parent so the dashboard can include property
  // values in its KPI rollup without a duplicate fetch.
  onPropertiesLoad?: (data: { properties: Property[] }) => void;
}) {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<FormState>(emptyForm);

  async function load() {
    setError(null);
    try {
      const res = await listProperties(accountId);
      setProperties(res.properties);
      onPropertiesLoad?.({ properties: res.properties });
    } catch (err) {
      setError(asMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!accountId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    const input = formToInput(form);
    if (!input) {
      setError('Name, owner, and current value are required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await createProperty(accountId, input);
      setForm(emptyForm);
      setShowAdd(false);
      await load();
    } catch (err) {
      setError(asMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  function startEdit(p: Property) {
    setEditingId(p.id);
    setEditForm(propertyToForm(p));
  }

  async function onSaveEdit(e: FormEvent, propertyId: string) {
    e.preventDefault();
    const input = formToInput(editForm);
    if (!input) {
      setError('Name, owner, and current value are required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await updateProperty(accountId, propertyId, input);
      setEditingId(null);
      await load();
    } catch (err) {
      setError(asMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function onDelete(p: Property) {
    if (!window.confirm(`Delete ${p.name}? This cannot be undone.`)) return;
    setError(null);
    try {
      await deleteProperty(accountId, p.id);
      await load();
    } catch (err) {
      setError(asMessage(err));
    }
  }

  if (loading) return <p style={{ color: '#64748b', margin: 0 }}>Loading…</p>;

  return (
    <div>
      {error && <p style={{ color: '#b91c1c', marginTop: 0 }}>{error}</p>}

      <div style={{ marginBottom: '0.75rem' }}>
        <button onClick={() => setShowAdd((s) => !s)}>{showAdd ? 'Cancel' : 'Add property'}</button>
      </div>

      {showAdd && (
        <PropertyForm
          form={form}
          setForm={setForm}
          submitting={submitting}
          onSubmit={onCreate}
          submitLabel="Save property"
        />
      )}

      {properties.length === 0 ? (
        <p style={{ color: '#64748b', margin: 0 }}>
          No properties yet — add one to track its value alongside investments.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {properties.map((p) => {
            const gain = p.current_value_pence - p.purchase_price_pence;
            const equity = p.current_value_pence - p.mortgage_balance_pence;
            const gainPct =
              p.purchase_price_pence > 0 ? (gain / p.purchase_price_pence) * 100 : null;
            const isEditing = editingId === p.id;
            return (
              <div
                key={p.id}
                style={{
                  background: '#eef2ff',
                  border: '1px solid #c7d2fe',
                  borderLeft: '4px solid #818cf8',
                  borderRadius: 8,
                  padding: '0.75rem 0.9rem',
                }}
              >
                {isEditing ? (
                  <PropertyForm
                    form={editForm}
                    setForm={setEditForm}
                    submitting={submitting}
                    onSubmit={(e) => onSaveEdit(e, p.id)}
                    onCancel={() => setEditingId(null)}
                    submitLabel="Save"
                    compact
                  />
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      gap: '1rem',
                      flexWrap: 'wrap',
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem' }}>
                        <strong style={{ fontSize: '1rem' }}>{p.name}</strong>
                        {p.kind && (
                          <span style={{ color: '#64748b', fontSize: '0.85rem' }}>· {p.kind}</span>
                        )}
                        {formatOwnerSummary(p) && (
                          <span style={{ color: '#64748b', fontSize: '0.85rem' }}>
                            · {formatOwnerSummary(p)}
                          </span>
                        )}
                      </div>
                      <div style={{ color: '#475569', fontSize: '0.82rem', marginTop: '0.15rem' }}>
                        {formatGBP(p.current_value_pence)}
                        {p.value_as_of && (
                          <span style={{ color: '#94a3b8' }}> as of {p.value_as_of}</span>
                        )}
                        {p.purchase_price_pence > 0 && (
                          <>
                            {' · '}
                            <span
                              style={{
                                color: gain >= 0 ? '#059669' : '#dc2626',
                                fontWeight: 500,
                              }}
                            >
                              {gain >= 0 ? '+' : ''}
                              {formatGBP(gain)}
                              {gainPct != null && (
                                <>
                                  {' '}
                                  ({gain >= 0 ? '+' : ''}
                                  {gainPct.toFixed(2)}%)
                                </>
                              )}
                            </span>
                            <span style={{ color: '#94a3b8' }}>
                              {' '}
                              since {p.purchase_date || 'purchase'}
                            </span>
                          </>
                        )}
                        {p.mortgage_balance_pence > 0 && (
                          <>
                            {' · mortgage '}
                            {formatGBP(p.mortgage_balance_pence)}
                            <span style={{ color: '#94a3b8' }}> · equity {formatGBP(equity)}</span>
                          </>
                        )}
                      </div>
                      {p.address && (
                        <div style={{ color: '#94a3b8', fontSize: '0.78rem', marginTop: '0.2rem' }}>
                          {p.address}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button onClick={() => startEdit(p)}>Edit</button>
                      <button onClick={() => onDelete(p)}>Delete</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------

function PropertyForm({
  form,
  setForm,
  submitting,
  onSubmit,
  onCancel,
  submitLabel,
  compact = false,
}: {
  form: FormState;
  setForm: (f: FormState) => void;
  submitting: boolean;
  onSubmit: (e: FormEvent) => void;
  onCancel?: () => void;
  submitLabel: string;
  compact?: boolean;
}) {
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm({ ...form, [key]: value });
  return (
    <form
      onSubmit={onSubmit}
      style={{
        background: compact ? 'transparent' : '#f8fafc',
        border: compact ? 'none' : '1px solid #e2e8f0',
        borderRadius: 8,
        padding: compact ? 0 : '1rem',
        marginBottom: compact ? 0 : '0.75rem',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '0.6rem',
      }}
    >
      <Field label="Name">
        <input value={form.name} onChange={(e) => set('name', e.target.value)} required />
      </Field>
      <div style={{ gridColumn: '1 / -1' }}>
        <OwnersEditor
          owners={form.owners}
          onChange={(next) => set('owners', next)}
          disabled={submitting}
        />
      </div>
      <Field label="Kind">
        <input
          value={form.kind}
          onChange={(e) => set('kind', e.target.value)}
          list="property-kind-options"
        />
        <datalist id="property-kind-options">
          {KIND_SUGGESTIONS.map((k) => (
            <option key={k} value={k} />
          ))}
        </datalist>
      </Field>
      <Field label="Address (optional)">
        <input value={form.address} onChange={(e) => set('address', e.target.value)} />
      </Field>
      <Field label="Current value (£)">
        <input
          inputMode="decimal"
          value={form.current_value_pounds}
          onChange={(e) => set('current_value_pounds', e.target.value)}
          required
        />
      </Field>
      <Field label="Valued on">
        <input
          type="date"
          value={form.value_as_of}
          onChange={(e) => set('value_as_of', e.target.value)}
        />
      </Field>
      <Field label="Purchase price (£)">
        <input
          inputMode="decimal"
          value={form.purchase_price_pounds}
          onChange={(e) => set('purchase_price_pounds', e.target.value)}
        />
      </Field>
      <Field label="Purchase date">
        <input
          type="date"
          value={form.purchase_date}
          onChange={(e) => set('purchase_date', e.target.value)}
        />
      </Field>
      <Field label="Mortgage balance (£)">
        <input
          inputMode="decimal"
          value={form.mortgage_balance_pounds}
          onChange={(e) => set('mortgage_balance_pounds', e.target.value)}
        />
      </Field>
      <Field label="Mortgage rate (% / yr)">
        <input
          type="number"
          step="0.01"
          min="0"
          value={form.mortgage_interest_rate_pct}
          onChange={(e) => set('mortgage_interest_rate_pct', e.target.value)}
          placeholder="4.5"
        />
      </Field>
      <Field label="Monthly payment (£)">
        <input
          inputMode="decimal"
          value={form.mortgage_monthly_payment_pounds}
          onChange={(e) => set('mortgage_monthly_payment_pounds', e.target.value)}
          placeholder="1500.00"
        />
      </Field>
      <Field label="Notes">
        <input value={form.notes} onChange={(e) => set('notes', e.target.value)} />
      </Field>
      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '0.5rem' }}>
        <button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : submitLabel}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        fontSize: '0.78rem',
        color: '#475569',
      }}
    >
      <span style={{ marginBottom: '0.2rem' }}>{label}</span>
      {children}
    </label>
  );
}

// OwnersEditor renders a list of (name, share%) rows with add/remove
// controls. Shows the running sum so the user can spot when shares
// don't add up to 100 — submit-time validation lives in formToInput.
function OwnersEditor({
  owners,
  onChange,
  disabled,
}: {
  owners: FormOwnerRow[];
  onChange: (next: FormOwnerRow[]) => void;
  disabled?: boolean;
}) {
  const sum = owners.reduce((s, o) => s + (parseFloat(o.share_pct) || 0), 0);
  const sumOff = Math.abs(sum - 100) > 0.5;

  function setRow(i: number, patch: Partial<FormOwnerRow>) {
    const next = [...owners];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  }
  function addRow() {
    // Default the new row's share to whatever's needed to reach 100
    // (or 0 if we're already over) — saves the user a step in the
    // common 50/50 → 60/40 cases.
    const remaining = Math.max(0, 100 - sum);
    onChange([...owners, { name: '', share_pct: trimShareString(remaining) }]);
  }
  function removeRow(i: number) {
    if (owners.length <= 1) return;
    onChange(owners.filter((_, idx) => idx !== i));
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: '0.3rem',
        }}
      >
        <span style={{ fontSize: '0.78rem', color: '#475569' }}>Owners</span>
        <span
          style={{
            fontSize: '0.75rem',
            color: sumOff ? '#dc2626' : '#64748b',
          }}
        >
          {sum.toFixed(2)}% allocated{sumOff ? ' — must equal 100' : ''}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
        {owners.map((o, i) => (
          <div key={i} style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
            <input
              value={o.name}
              onChange={(e) => setRow(i, { name: e.target.value })}
              placeholder="Name"
              required
              disabled={disabled}
              style={{ flex: 2, padding: '0.35rem 0.5rem' }}
            />
            <input
              value={o.share_pct}
              onChange={(e) => setRow(i, { share_pct: e.target.value })}
              placeholder="50"
              required
              disabled={disabled}
              inputMode="decimal"
              style={{ width: 70, padding: '0.35rem 0.5rem' }}
            />
            <span style={{ color: '#64748b', fontSize: '0.85rem' }}>%</span>
            <button
              type="button"
              onClick={() => removeRow(i)}
              disabled={disabled || owners.length <= 1}
              title="Remove owner"
              style={{ padding: '0.3rem 0.6rem' }}
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addRow}
        disabled={disabled}
        style={{ marginTop: '0.4rem', padding: '0.3rem 0.7rem', fontSize: '0.8rem' }}
      >
        + Add owner
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formToInput(f: FormState): PropertyInput | null {
  if (!f.name.trim()) return null;
  const owners = parseOwners(f.owners);
  if (!owners) return null;
  const current = parsePounds(f.current_value_pounds);
  if (current == null || current < 0) return null;
  const purchase = parsePounds(f.purchase_price_pounds) ?? 0;
  const mortgage = parsePounds(f.mortgage_balance_pounds) ?? 0;
  const ratePct =
    f.mortgage_interest_rate_pct.trim() === '' ? 0 : Number(f.mortgage_interest_rate_pct);
  const monthly = parsePounds(f.mortgage_monthly_payment_pounds) ?? 0;
  if (!Number.isFinite(ratePct) || ratePct < 0 || monthly < 0) return null;
  return {
    name: f.name.trim(),
    owners,
    kind: f.kind.trim() || undefined,
    address: f.address.trim() || undefined,
    purchase_price_pence: Math.round(purchase * 100),
    purchase_date: f.purchase_date.trim() || undefined,
    current_value_pence: Math.round(current * 100),
    value_as_of: f.value_as_of.trim() || undefined,
    mortgage_balance_pence: Math.round(mortgage * 100),
    mortgage_interest_rate_pct: ratePct || undefined,
    mortgage_monthly_payment_pence: monthly > 0 ? Math.round(monthly * 100) : undefined,
    notes: f.notes.trim() || undefined,
  };
}

function propertyToForm(p: Property): FormState {
  return {
    name: p.name,
    owners: ownersFromProperty(p).map((o) => ({
      name: o.name,
      share_pct: trimShareString(o.share_pct),
    })),
    kind: p.kind ?? '',
    address: p.address ?? '',
    purchase_price_pounds: pencesToPoundsString(p.purchase_price_pence),
    purchase_date: p.purchase_date ?? '',
    current_value_pounds: pencesToPoundsString(p.current_value_pence),
    value_as_of: p.value_as_of ?? todayISO(),
    mortgage_balance_pounds: pencesToPoundsString(p.mortgage_balance_pence),
    mortgage_interest_rate_pct:
      p.mortgage_interest_rate_pct != null && p.mortgage_interest_rate_pct > 0
        ? String(p.mortgage_interest_rate_pct)
        : '',
    mortgage_monthly_payment_pounds:
      p.mortgage_monthly_payment_pence != null && p.mortgage_monthly_payment_pence > 0
        ? pencesToPoundsString(p.mortgage_monthly_payment_pence)
        : '',
    notes: p.notes ?? '',
  };
}

// parseOwners coerces the form's string-shaped rows into the API's
// owner shape, validating along the way: every name set, every share
// strictly positive, sum within ±0.5 of 100. Returns null on failure
// (caller surfaces a generic error).
function parseOwners(rows: FormOwnerRow[]): PropertyOwner[] | null {
  if (rows.length === 0) return null;
  const out: PropertyOwner[] = [];
  let sum = 0;
  for (const r of rows) {
    const name = r.name.trim();
    if (!name) return null;
    const share = parseFloat(r.share_pct);
    if (!Number.isFinite(share) || share <= 0 || share > 100) return null;
    sum += share;
    out.push({ name, share_pct: share });
  }
  if (sum < 99.5 || sum > 100.5) return null;
  return out;
}

// ownersFromProperty bridges the legacy single-owner case so existing
// rows (saved before multi-owner shipped) edit correctly.
function ownersFromProperty(p: Property): PropertyOwner[] {
  if (p.owners && p.owners.length > 0) return p.owners;
  if (p.owner) return [{ name: p.owner, share_pct: 100 }];
  return [{ name: '', share_pct: 100 }];
}

function trimShareString(n: number): string {
  // Strip trailing zeros so 50 displays as "50" not "50.00", but keep
  // fractional shares (33.33) intact.
  return Number.isInteger(n) ? String(n) : n.toString();
}

function formatOwnerSummary(p: Property): string {
  const owners =
    p.owners && p.owners.length > 0 ? p.owners : p.owner ? [{ name: p.owner, share_pct: 100 }] : [];
  if (owners.length === 0) return '';
  if (owners.length === 1 && owners[0].share_pct >= 99.5) return owners[0].name;
  return owners.map((o) => `${o.name} ${trimShareString(o.share_pct)}%`).join(' · ');
}

function parsePounds(s: string): number | null {
  const trimmed = s.trim().replace(/,/g, '');
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

function pencesToPoundsString(pence: number): string {
  if (!pence) return '';
  return (pence / 100).toFixed(2);
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function asMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Unknown error';
}

const gbp = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  maximumFractionDigits: 2,
});

function formatGBP(pence: number): string {
  return gbp.format(pence / 100);
}
